import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";
import type { CognitoSignUpRequestMetadata } from "@/lib/gql/__generated__/graphql";

import { QUERY_PARAMS } from "@/routes/_constants/query-params";
import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";

/**
 * サインアップ時にユーザーから入力を受け取るパラメーター
 */
export type SignUpParams = {
	email: string;
	password: string;
	clientMetadata: Omit<CognitoSignUpRequestMetadata, "callbackUrl" | "codeParameter">;
	successCallback?: () => void;
};

/**
 * サインアップ時に副作用として発生するパラメーター
 * @param email
 * @returns
 */
const getCallbackUrlAndCodeParameter = (
	email: string,
): Pick<CognitoSignUpRequestMetadata, "callbackUrl" | "codeParameter"> => {
	const origin = window.location.origin;
	const codeParameter = "{####}";
	const callbackUrl = `${origin}${PATHS.AUTH.VERIFY}?${QUERY_PARAMS.AUTH.SIGN_UP_VERIFY.USERNAME}=${encodeURIComponent(email)}&${QUERY_PARAMS.AUTH.SIGN_UP_VERIFY.VERIFY_CODE}=${codeParameter}`;
	return { callbackUrl, codeParameter };
};

export const useSignUp = () => {
	const navigate = useNavigate();

	const signUp = async ({ email, password, clientMetadata: _clientMetadata, successCallback }: SignUpParams) => {
		const { callbackUrl, codeParameter } = getCallbackUrlAndCodeParameter(email);
		const clientMetadata: CognitoSignUpRequestMetadata = { ..._clientMetadata, callbackUrl, codeParameter };

		try {
			const res = await AuthWrapper.authSignUp(email, password, clientMetadata);

			/**
			 * @see file://./../_md/next-step/sign-up-step.md
			 */
			if (res.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
				navigate(PATHS.AUTH.VERIFY_YOUR_EMAIL);
				successCallback?.();
			} else {
				/**
				 * @description
				 * sign-up のフェーズでは、以下の step が送られることはあり得ない。
				 * - DONE
				 * - COMPLETE_AUTO_SIGN_IN
				 */
				notifyUserEventInfo({
					variant: "error",
					title: "予期せぬエラーが発生しました",
					description: "カスタマーサポートへお問い合わせください。",
				});
			}
		} catch (err) {
			const result = isAuthError(err);
			if (result) {
				/**
				 *
				 * @description
				 * amplify/auth から throw されるエラーは以下、
				 * 1. SignUpException
				 * 2. AuthValidationErrorCode @see {@link(https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html)}
				 * 3. AuthTokenConfigException
				 *
				 * 2,3 はクライアントでハンドリングしているため、1のみ考慮する。
				 */
				const errorType = err.name;
				if (errorType === "UsernameExistsException") {
					notifyUserEventInfo({
						variant: "error",
						title: "すでにアカウントが存在します",
						description: "別のメールアドレスでもう一度お試しください。",
					});
				} else if (errorType === "AliasExistsException") {
					notifyUserEventInfo({
						variant: "error",
						title: "無効なメールアドレスです",
						description: "別のメールアドレスをご利用ください",
					});
				} else if (
					errorType === "TooManyFailedAttemptsException" ||
					errorType === "TooManyRequestsException" ||
					errorType === "LimitExceededException"
				) {
					notifyUserEventInfo({
						variant: "error",
						title: "アカウント作成に失敗しました",
						description: "時間を空けて、もう一度お試しください。",
					});
				} else if (errorType === "InvalidPasswordException") {
					notifyUserEventInfo({
						variant: "error",
						title: "パスワードを確認してください",
						description: "パスワードは8文字以上で、英大文字・英小文字・数字を含めてください。",
					});
				} else {
					notifyUserEventInfo({
						variant: "error",
						title: "アカウント作成に失敗しました",
						description: "カスタマーサポートへお問い合わせください。",
					});
				}
			} else {
				notifyUserEventInfo({
					variant: "error",
					title: "アカウント作成に失敗しました",
					description: "もう一度お試しください。",
				});
			}
		}
	};

	return { signUp };
};
